import { parse, format, getTime, formatDistanceToNow } from 'date-fns';

type InputValue = Date | string | number | null | undefined;

export function fDate(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy';

  return date ? format(new Date(date), fm) : '';
}

export function fISODateString(date: Date | null) {
  if (!date) return '';
  try {
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
}

export function fTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'p';

  return date ? format(new Date(date), fm) : '';
}

export function fDateTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy p';

  return date ? format(new Date(date), fm) : '';
}

export function fTimestamp(date: InputValue) {
  return date ? getTime(new Date(date)) : '';
}

export function fToNow(date: InputValue) {
  return date
    ? formatDistanceToNow(new Date(date), {
        addSuffix: true,
      })
    : '';
}

export function isBetween(inputDate: Date | string | number, startDate: Date, endDate: Date) {
  const date = new Date(inputDate);

  const results =
    new Date(date.toDateString()) >= new Date(startDate.toDateString()) &&
    new Date(date.toDateString()) <= new Date(endDate.toDateString());

  return results;
}

export function isAfter(startDate: Date | null, endDate: Date | null) {
  const results =
    startDate && endDate ? new Date(startDate).getTime() > new Date(endDate).getTime() : false;

  return results;
}

export function convertTimeToUtc(timeString: string): string {
  if (!timeString) return '';

  try {
    const today = new Date();
    const localTime = parse(timeString, 'HH:mm:ss', today);

    const utcTime = new Date(localTime.getTime() + localTime.getTimezoneOffset() * 60000);

    return format(utcTime, 'HH:mm:ss');
  } catch (error) {
    console.error('Error converting time to UTC:', error);
    return timeString;
  }
}

export function convertUtcToLocalTime(utcTimeString: string): string {
  if (!utcTimeString) return '';

  try {
    const today = new Date();
    const utcTime = parse(utcTimeString, 'HH:mm:ss', today);

    const localTime = new Date(utcTime.getTime() - utcTime.getTimezoneOffset() * 60000);

    return format(localTime, 'HH:mm:ss');
  } catch (error) {
    console.error('Error converting UTC time to local:', error);
    return utcTimeString;
  }
}

// Calendar-day convention (AC-2072): pickers produce Dates at local midnight,
// which serialise onto the previous UTC day east of UTC. Day-precision values
// are written as UTC day boundaries (toUtcDayStart/toUtcDayEnd) and read back
// with fromUtcDay/fUtcDay, which round to the nearest UTC day so legacy
// local-midnight values still decode to the day that was picked. Day-end
// boundaries are write-only: reading one back rounds to the next day.
const BARE_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function toUtcDayStart(date: Date | string | null | undefined): string | undefined {
  if (!date) return undefined;
  if (typeof date === 'string' && BARE_DAY_PATTERN.test(date)) return `${date}T00:00:00.000Z`;
  const parsed = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(parsed.getTime())) return undefined;
  return format(parsed, "yyyy-MM-dd'T'00:00:00.000'Z'");
}

export function toUtcDayEnd(date: Date | string | null | undefined): string | undefined {
  if (!date) return undefined;
  if (typeof date === 'string' && BARE_DAY_PATTERN.test(date)) return `${date}T23:59:59.999Z`;
  const parsed = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(parsed.getTime())) return undefined;
  return format(parsed, "yyyy-MM-dd'T'23:59:59.999'Z'");
}

const DAY_MS = 86_400_000;

export function fromUtcDay(date: Date | string | null | undefined): Date | null {
  if (!date) return null;
  const parsed = typeof date === 'string' ? new Date(date) : date;
  const time = parsed.getTime();
  if (Number.isNaN(time)) return null;
  const nearestUtcDay = new Date(Math.round(time / DAY_MS) * DAY_MS);
  return new Date(
    nearestUtcDay.getUTCFullYear(),
    nearestUtcDay.getUTCMonth(),
    nearestUtcDay.getUTCDate()
  );
}

export function fUtcDay(date: Date | string | null | undefined, newFormat?: string): string {
  const day = fromUtcDay(date);
  return day ? format(day, newFormat || 'dd MMM yyyy') : '';
}

export function convertUtcToLocalTimeWithDate(utcTimeString: string, date: Date): Date {
  if (!utcTimeString) return date;

  try {
    const [hours, minutes] = utcTimeString.split(':');

    // Create a UTC date with the specified time
    const utcDateTime = new Date(date);
    utcDateTime.setUTCHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    // Convert to local time - this automatically handles day transitions
    return new Date(utcDateTime.getTime());
  } catch (error) {
    console.error('Error converting UTC time to local with date:', error);
    return date;
  }
}
